import React, { memo, ReactElement } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { View, TouchableWithoutFeedback } from 'react-native';
import { useGetUser } from '_api/useUsers';
import Images from '_images';
import { fromNowFormat, WW } from '_utils';
import hexToRgba from '_utils/hexToRgba';
import { useSelectedCoopItem } from '_utils/hooks';
import { getUsernameFromProfile } from '_utils/misc';
import { screenMargin, smallestFontSize, smallestMargin } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { CacheImage, ListItem, HeimeText, Icon } from 'Components';
import ReadMoreRichText from 'Components/RichText/ReadMoreRichText';
import TouchableHeime from 'Components/TouchableHeime';
import { ApiImage, FileImage, RichText } from 'types/Base';
import { UnixTimeCode } from 'types/Utility';
import ImagePairDisplay from './components/ImagePairDisplay';
import ApartmentGroupsDisplay from '../../../ApartmentGroupsDisplay';
import NewTag from '../common/NewTag';
import useIsNewDisplayState from '../common/useIsNewDisplayState';

interface RequestItemProps {
    title: string;
    content: RichText | null;
    creator: number;
    fromBoard: boolean;
    created: UnixTimeCode;
    isNew: boolean;
    onPress: (expanded: boolean) => void;
    commentsLength: number;
    showComments: boolean;
    attachmentsLength: number;
    images: Array<ApiImage | FileImage>;
    apartment_groups: { id: string; name: string }[];
    isFirst: boolean;
    isVisible?: boolean;
    id: number;
}

const RequestItem = ({
    title,
    content,
    creator,
    fromBoard = false,
    created,
    isNew,
    onPress,
    commentsLength,
    showComments,
    attachmentsLength,
    images,
    apartment_groups,
    isFirst,
    isVisible = false,
    id,
}: RequestItemProps): ReactElement => {
    const { theme } = useThemeContext();
    const { t } = useTranslation();
    const coopType = useSelectedCoopItem()?.type ?? 'joint_ownership';

    const backgroundColor = fromBoard ? theme.lightGreen : undefined;
    const borderColor = fromBoard ? hexToRgba(theme.main, 0.1) : theme.mediumBackground;

    const displayIsNew = useIsNewDisplayState(isNew, id);
    const newTag = (
        <NewTag isNew={displayIsNew} variant={fromBoard ? 'darkGreen' : 'lightGreen'} isVisible={isVisible} />
    );
    const displayNewInFooter = attachmentsLength || commentsLength || fromBoard;

    return (
        <TouchableHeime
            onPress={() => onPress(!showComments || commentsLength === 0)}
            style={{
                width: '100%',
                minHeight: screenMargin,
                paddingTop: smallestMargin,
                paddingLeft: screenMargin,
                paddingRight: screenMargin,
                borderTopWidth: isFirst ? 0 : screenMargin / 2,
                borderTopColor: theme.lightBackground,
                backgroundColor,
                marginTop: isFirst ? smallestMargin : undefined,
            }}
        >
            {fromBoard && <BoardLabel boardNoun={t(`typeSpecific:${coopType}:boardNounDefiniteArticle`)} />}
            <ApartmentGroupsDisplay apartment_groups={apartment_groups} />
            <HeimeText variant="title" style={{ textAlign: 'left' }}>
                {title}
            </HeimeText>
            <HeimeText style={{ color: theme.darkGray, fontSize: smallestFontSize, marginBottom: smallestMargin }}>
                {fromNowFormat(moment.unix(created).toDate())}
            </HeimeText>
            <TouchableWithoutFeedback>
                <View>
                    <ImagePairDisplay images={images} />
                    {content && (
                        <ReadMoreRichText
                            onReadMorePress={() => {
                                onPress(true);
                                return false;
                            }}
                            numberOfLines={5}
                            text={content}
                        />
                    )}
                </View>
            </TouchableWithoutFeedback>
            {!fromBoard && (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <UserInfo creator={creator} />
                    {!displayNewInFooter && displayIsNew ? newTag : null}
                </View>
            )}
            <Footer
                commentsLength={commentsLength}
                showComments={showComments}
                attachmentsLength={attachmentsLength}
                right={displayNewInFooter && displayIsNew ? newTag : null}
                borderColor={borderColor}
                isBoard={fromBoard}
            />
        </TouchableHeime>
    );
};

const BoardLabel = ({ boardNoun }: { boardNoun: string }) => {
    const { t } = useTranslation();
    const { theme } = useThemeContext();

    return (
        <View style={{ alignItems: 'flex-start' }}>
            <View
                style={{
                    backgroundColor: theme.main,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingLeft: smallestMargin,
                    paddingRight: smallestMargin,
                    paddingTop: 2,
                    paddingBottom: 2,
                    borderRadius: WW * 0.04,
                    marginBottom: smallestMargin,
                    gap: 2,
                }}
            >
                <Icon name="board" color="mainText" style={{ width: smallestFontSize, height: smallestFontSize }} />
                <HeimeText style={{ fontSize: smallestFontSize, color: theme.mainText }}>
                    {t('sharingAll:fromBoard', {
                        boardNoun,
                    })}
                </HeimeText>
            </View>
        </View>
    );
};

const UserInfo = ({ creator }: { creator: number }) => {
    const { theme } = useThemeContext();
    const user = useGetUser(creator);

    return (
        <ListItem
            containerStyle={{
                borderBottomWidth: 0,
                paddingTop: smallestMargin,
                paddingBottom: 0,
                marginBottom: smallestMargin,
            }}
            containerWrapper={{
                justifyContent: 'center',
                marginLeft: smallestMargin,
                marginRight: 0,
            }}
            avatar={
                <CacheImage
                    source={user?.avatar ?? Images.defaultAvatar}
                    style={{
                        width: WW * 0.08,
                        height: WW * 0.08,
                        borderRadius: (WW * 0.08) / 2,
                        overflow: 'hidden',
                    }}
                />
            }
            title={getUsernameFromProfile(user)}
            titleStyle={{
                fontSize: smallestFontSize,
                color: theme.darkGray,
                fontWeight: 'normal',
            }}
        />
    );
};

const Footer = ({
    commentsLength,
    showComments,
    attachmentsLength,
    right,
    borderColor,
    isBoard,
}: {
    commentsLength: number;
    showComments: boolean;
    attachmentsLength: number;
    right: ReactElement | null;
    borderColor: string;
    isBoard: boolean;
}) => {
    const { t } = useTranslation();
    const { theme } = useThemeContext();

    if (!commentsLength && !attachmentsLength && !right) {
        if (isBoard) {
            return <View style={{ height: smallestMargin }} />;
        }
        return null;
    }

    return (
        <View
            style={{
                flexDirection: 'row',
                alignItems: 'stretch',
                flexWrap: 'wrap',
                marginBottom: smallestMargin,
                paddingTop: smallestMargin,
                borderTopWidth: 1,
                borderTopColor: borderColor,
                minHeight: WW * 0.1,
            }}
        >
            {showComments && commentsLength ? (
                <View
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                        display: 'flex',
                        borderRadius: WW * 0.04,
                        marginRight: smallestMargin,
                        paddingVertical: WW * 0.01,
                        height: WW * 0.08,
                    }}
                >
                    <Icon name="comment" color="main" />
                    <HeimeText
                        style={{
                            fontSize: smallestFontSize,
                            color: theme.main,
                            marginLeft: smallestMargin,
                        }}
                    >
                        {commentsLength === 1
                            ? t('sharingAll:comment')
                            : t('sharingAll:comments', { count: commentsLength })}
                    </HeimeText>
                </View>
            ) : null}
            {attachmentsLength ? (
                <View
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                        display: 'flex',
                        borderRadius: WW * 0.04,
                        marginRight: smallestMargin,
                        paddingVertical: WW * 0.01,
                        height: WW * 0.08,
                    }}
                >
                    <Icon name="attachment" color="main" />
                    <HeimeText
                        style={{
                            fontSize: smallestFontSize,
                            color: theme.main,
                            marginLeft: smallestMargin,
                        }}
                    >
                        {attachmentsLength === 1
                            ? t('sharingAll:attachment')
                            : t('sharingAll:attachments', { count: attachmentsLength })}
                    </HeimeText>
                </View>
            ) : null}
            {right}
        </View>
    );
};

export default memo(RequestItem);
